import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { formatCurrency } from '@/utils/currency';
export default function ModifierDialog({ 
  open, 
  onOpenChange, 
  menuItem, 
  selectedVariant,
  setSelectedVariant,
  selectedModifiers,
  setSelectedModifiers,
  selectedAddOns,
  setSelectedAddOns,
  itemNote,
  setItemNote,
  onAdd
}) {
  if (!menuItem) return null;

  const handleModifierSelect = (groupName, option) => {
    setSelectedModifiers({
      ...selectedModifiers,
      [groupName]: {
        group_name: groupName,
        option_name: option.name,
        option_price: option.price
      }
    });
  };

  const handleAddOnToggle = (addon) => {
    const exists = selectedAddOns.find(a => a.name === addon.name);
    if (exists) {
      setSelectedAddOns(selectedAddOns.filter(a => a.name !== addon.name));
    } else {
      setSelectedAddOns([...selectedAddOns, { name: addon.name, price: addon.price }]);
    }
  };

  const calculateTotal = () => {
    const basePrice = selectedVariant
      ? selectedVariant.dine_in || selectedVariant.takeaway || selectedVariant.delivery || 0
      : menuItem.pricing?.dine_in || menuItem.price || 0;
    const modifierPrice = Object.values(selectedModifiers).reduce((sum, mod) => sum + (mod.option_price || 0), 0);
    const addOnPrice = selectedAddOns.reduce((sum, addon) => sum + addon.price, 0);
    return basePrice + modifierPrice + addOnPrice;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{menuItem.name}</DialogTitle>
          <p className="text-sm text-gray-600">{menuItem.description}</p>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Variant Selection */}
          {menuItem.pricing?.has_variants && menuItem.pricing?.variants?.length > 0 && (
            <div className="border rounded-lg p-4">
              <Label className="text-base font-semibold mb-3 block">
                Select Variant
              </Label>
              <RadioGroup
                value={selectedVariant?.id || ""}
                onValueChange={(value) => {
                  const variant = menuItem.pricing.variants.find(
                    (variant) => variant.id === value
                  );
                  if (variant) {
                    setSelectedVariant(variant);
                  }
                }}
              >
                <div className="space-y-2">
                  {menuItem.pricing.variants.map((variant) => (
                    <div key={variant.id} className="flex items-center space-x-2">
                      <RadioGroupItem value={variant.id} id={`variant-${variant.id}`} />
                      <label htmlFor={`variant-${variant.id}`} className="flex-1 cursor-pointer">
                        {variant.name}
                        <span className="text-green-600 ml-2">
                          {formatCurrency(
                            selectedVariant?.id === variant.id
                              ? selectedVariant?.dine_in || selectedVariant?.takeaway || selectedVariant?.delivery || 0
                              : variant?.dine_in || variant?.takeaway || variant?.delivery || 0
                          )}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Modifier Groups */}
          {menuItem.modifier_groups && menuItem.modifier_groups.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Customize Your Order</h3>
              {menuItem.modifier_groups.map((group, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <Label className="text-base font-semibold mb-3 block">
                    {group.name} {group.required && <span className="text-red-500">*</span>}
                  </Label>
                  
                  {group.multi_select ? (
                    // Multi-select with checkboxes
                    <div className="space-y-2">
                      {group.options.map((option, optIndex) => (
                        <div key={optIndex} className="flex items-center space-x-2">
                          <Checkbox
                            id={`${group.name}-${optIndex}`}
                            checked={selectedModifiers[group.name]?.option_name === option.name}
                            onCheckedChange={() => handleModifierSelect(group.name, option)}
                          />
                          <label htmlFor={`${group.name}-${optIndex}`} className="flex-1 cursor-pointer">
                            {option.name}
                            {option.price > 0 && <span className="text-green-600 ml-2">+{formatCurrency(option.price)}</span>}
                          </label>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Single select with radio buttons
                    <RadioGroup
                      value={selectedModifiers[group.name]?.option_name}
                      onValueChange={(value) => {
                        const option = group.options.find(o => o.name === value);
                        if (option) handleModifierSelect(group.name, option);
                      }}
                    >
                      {group.options.map((option, optIndex) => (
                        <div key={optIndex} className="flex items-center space-x-2">
                          <RadioGroupItem value={option.name} id={`${group.name}-radio-${optIndex}`} />
                          <label htmlFor={`${group.name}-radio-${optIndex}`} className="flex-1 cursor-pointer">
                            {option.name}
                            {option.price > 0 && <span className="text-green-600 ml-2">+{formatCurrency(option.price)}</span>}
                          </label>
                        </div>
                      ))}
                    </RadioGroup>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add-ons */}
          {menuItem.add_ons && menuItem.add_ons.length > 0 && (
            <div className="border rounded-lg p-4">
              <Label className="text-base font-semibold mb-3 block">Add-ons</Label>
              <div className="space-y-2">
                {menuItem.add_ons.map((addon, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Checkbox
                      id={`addon-${index}`}
                      checked={selectedAddOns.some(a => a.name === addon.name)}
                      onCheckedChange={() => handleAddOnToggle(addon)}
                    />
                    <label htmlFor={`addon-${index}`} className="flex-1 cursor-pointer">
                      {addon.name} <span className="text-green-600">+{formatCurrency(addon.price)}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Special Instructions */}
          <div>
            <Label htmlFor="special-notes" className="text-base font-semibold mb-2 block">
              Special Instructions
            </Label>
            <Textarea
              id="special-notes"
              placeholder="e.g., No onion, less salt..."
              value={itemNote}
              onChange={(e) => setItemNote(e.target.value)}
              rows={3}
            />
          </div>

          {/* Total & Add Button */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-lg font-bold">
              Total: <span className="text-red-600">{formatCurrency(calculateTotal())}</span>
            </div>
            <Button 
              onClick={onAdd}
              className="bg-red-600 hover:bg-red-700 text-white px-8"
              size="lg"
            >
              Add to Cart
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
